import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider, ApolloClient, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const PUBLIC_OPERATIONS = new Set([
  'Login',
  'VerifyOTP',
  'RequestAppointmentVerification',
  'CreateAppointmentWithVerification',
]);

const isJwtExpired = (token) => {
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) return true;
    const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch (error) {
    return true;
  }
};

// Créer un lien HTTP pour Apollo Client avec support des uploads
const httpLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:8000/graphql/',
});

// Ajouter le token d'authentification aux en-têtes
const authLink = setContext((operation, { headers }) => {
  if (PUBLIC_OPERATIONS.has(operation.operationName)) {
    return { headers: { ...headers, authorization: "" } };
  }

  // Récupérer le token depuis le localStorage
  let token = localStorage.getItem('token');
  if (token && isJwtExpired(token)) {
    localStorage.removeItem('token');
    token = null;
  }
  
  // Retourner les en-têtes avec le token si disponible
  return {
    headers: {
      ...headers,
      authorization: token ? `JWT ${token}` : "",
    }
  };
});

// Gérer les erreurs d'authentification
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      
      // Si l'erreur est liée à l'authentification, rediriger vers la page de connexion
      if (
        message.includes('authentification') ||
        message.includes('authentication') ||
        message.includes('Signature has expired') ||
        message.includes('token') ||
        message.includes('expired')
      ) {
        localStorage.removeItem('token');
        // Éviter une redirection en boucle si déjà sur la page de connexion
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    });
  }
  
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// Créer le client Apollo
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

ReactDOM.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ApolloProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(); 
